import { BLUE_200, BLUE_600, GRAY_300, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Divider, Stack, Typography } from '@mui/material'

import { AiLabelSection } from '../AiLabel'
import { FC, useMemo, useState } from 'react'

import { AnalyticsEvent } from 'utils/analytics'
import { EditionType } from '../photoStudioTypes'
import { GenerateButton } from '../common/GenerateButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MUITooltip } from 'components/common/MUITooltip'
import { NonSubscriberEnhanceUpsell } from './NonSubscriberEnhanceUpsell.component'
import PhotoFilterOutlinedIcon from '@mui/icons-material/PhotoFilterOutlined'
import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import { SkyStyleSelector } from './SkyStyleSelector.component'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import skyClearThumb from 'assets/img/ai_settings/clear.webp'
import skyCloudyThumb from 'assets/img/ai_settings/cloudy.webp'
import skyLowCloudThumb from 'assets/img/ai_settings/low_cloud.webp'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

export interface SkyStyleOption {
  id: string
  label: string
  previewImage: string
}

interface EnhancePanelProps {
  isDisabled?: boolean
  isProcessingImage?: boolean
  onGenerate?: (skyReplacement?: SkyReplacementOption | null) => void
}

/**
 * Enhance Panel for Photo Studio with AI auto-enhancement and sky replacement.
 * Shows two feature toggles: "Auto-enhance the photo" (always on) and "Replace sky" (toggle).
 * When sky replacement is enabled, a grid of sky style options (No cloud, Few clouds, Cloudy) appears.
 * Non-subscribers see an upsell card with before/after slider preview.
 *
 * Based on Figma: https://www.figma.com/design/4KaeWeL3snx2flCNKcT65T/Photo-Studio?node-id=4395-7443
 */
export const EnhancePanel: FC<EnhancePanelProps> = ({ isDisabled = false, isProcessingImage = false, onGenerate }) => {
  const { t } = useTranslation(['photo_studio_editing', 'ai_settings'])
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const { logPhotoStudioEvent, aiLabelState, setAiLabelToggle, setAiLabelType, setAiLabelText, setAiLabelColour, setAiLabelOpacity, resetAiLabelPlacement } = useClientPhotoStudio()

  const [isSkyReplacementEnabled, setIsSkyReplacementEnabled] = useState(false)
  const [selectedSkyStyle, setSelectedSkyStyle] = useState<string | null>(null)

  const skyStyles: SkyStyleOption[] = useMemo(
    () => [
      {
        id: SkyReplacementOption.NO_CLOUD,
        label: t('ai_settings:sky_replacement_options.CLEAR'),
        previewImage: skyClearThumb,
      },
      {
        id: SkyReplacementOption.LOW_CLOUD,
        label: t('ai_settings:sky_replacement_options.LOW_CLOUD'),
        previewImage: skyLowCloudThumb,
      },
      {
        id: SkyReplacementOption.CLOUDY,
        label: t('ai_settings:sky_replacement_options.HIGH_CLOUD'),
        previewImage: skyCloudyThumb,
      },
    ],
    [t]
  )

  const handleSkyReplacementToggle = () => {
    const newEnabled = !isSkyReplacementEnabled
    setIsSkyReplacementEnabled(newEnabled)
    if (newEnabled) {
      setSelectedSkyStyle(SkyReplacementOption.NO_CLOUD)
    } else {
      setSelectedSkyStyle(null)
    }
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
      editionType: EditionType.ENHANCE,
      editionSettings: {
        skyReplacementEnabled: newEnabled,
        skyReplacement: newEnabled ? 'Enabled sky replacement toggle - default value NO_CLOUD' : 'Sky replacement disabled',
      },
    })
  }

  const handleSkyStyleSelect = (styleId: string) => {
    const newStyle = styleId === selectedSkyStyle ? null : styleId
    setSelectedSkyStyle(newStyle)
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
      editionType: EditionType.ENHANCE,
      editionSettings: {
        skyReplacementEnabled: true,
        skyReplacement: newStyle,
      },
    })
  }

  const isGenerateDisabled = isProcessingImage || isDisabled

  if (!hasWorkspaceActiveSubscription) {
    return <NonSubscriberEnhanceUpsell skyStyles={skyStyles} />
  }

  return (
    <Stack
      sx={{
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '2.4rem 2.4rem 0 2.4rem',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Scrollable content area */}
      <Stack
        gap={0.8}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Auto-enhance toggle (always active indicator) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
            padding: '1.2rem 1.6rem',
            borderRadius: '10rem',
            backgroundColor: BLUE_200,
            cursor: 'default',
          }}
        >
          <PhotoFilterOutlinedIcon sx={{ fontSize: '2rem', color: GRAY_900 }} />
          <Typography
            sx={{
              flex: 1,
              fontSize: '1.4rem',
              fontWeight: 450,
              lineHeight: 1.4,
              letterSpacing: '-0.56px',
              color: GRAY_900,
            }}
          >
            {t('enhance.auto_enhance_label')}
          </Typography>
          <MUITooltip content={t('enhance.auto_enhance_tooltip')} placement="top">
            <InfoOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700, cursor: 'help', alignSelf: 'baseline' }} />
          </MUITooltip>
        </Box>

        {/* Replace sky toggle */}
        <Box
          onClick={() => !isDisabled && handleSkyReplacementToggle()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
            padding: '1.2rem',
            borderRadius: '0.8rem',
            border: `1px solid ${isSkyReplacementEnabled ? BLUE_600 : GRAY_300}`,
            backgroundColor: isSkyReplacementEnabled ? BLUE_200 : WHITE,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            transition: 'all .2s ease',
            '&:hover': {
              borderColor: isDisabled ? GRAY_300 : BLUE_600,
              backgroundColor: isDisabled ? WHITE : BLUE_200,
            },
          }}
        >
          <SyncOutlinedIcon sx={{ fontSize: '1.6rem', color: isSkyReplacementEnabled ? GRAY_900 : GRAY_700 }} />
          <Typography
            sx={{
              flex: 1,
              fontSize: '1.4rem',
              fontWeight: isSkyReplacementEnabled ? 450 : 350,
              lineHeight: 1.4,
              letterSpacing: '-0.56px',
              color: isSkyReplacementEnabled ? GRAY_900 : GRAY_700,
            }}
          >
            {t('enhance.replace_sky_label')}
          </Typography>
        </Box>

        {/* Sky style selector (visible when sky replacement enabled) */}
        {isSkyReplacementEnabled && (
          <>
            <Divider sx={{ borderColor: GRAY_300, marginTop: '0.8rem', marginBottom: '0.8rem' }} />
            <SkyStyleSelector skyStyles={skyStyles} selectedStyle={selectedSkyStyle} onStyleSelect={handleSkyStyleSelect} isDisabled={isDisabled} />
          </>
        )}

        {/* AI Label Section (EU AI Act) - optional in every Photo Studio tool */}
        <Box sx={{ marginTop: '1.6rem' }}>
          <AiLabelSection
            showAiLabel={aiLabelState.showAiLabel}
            onToggle={setAiLabelToggle}
            labelType={aiLabelState.labelType}
            onLabelTypeChange={setAiLabelType}
            labelText={aiLabelState.labelText}
            onLabelTextChange={setAiLabelText}
            colour={aiLabelState.colour}
            onColourChange={setAiLabelColour}
            opacity={aiLabelState.opacity}
            onOpacityChange={setAiLabelOpacity}
            onResetPlacement={resetAiLabelPlacement}
            isDisabled={isDisabled}
          />
        </Box>
      </Stack>

      {/* Generate Button - Fixed at bottom */}
      {onGenerate && <GenerateButton onGenerate={() => onGenerate(selectedSkyStyle as SkyReplacementOption | null)} disabled={isGenerateDisabled} disabledTooltip={isGenerateDisabled ? t('enhance.disabled_generate') : undefined} />}
    </Stack>
  )
}
